import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './S_ActivationCapsulePopup.module.css';
import O_Calendar from '@/components/O_Organisms/O_Calendar/O_Calendar';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import O_TimeCounter from '@/components/O_Organisms/O_TimeCounter/O_TimeCounter';
import { TimeCounterValue } from '@/hooks/useTimeCounter';
import { useIsMobile } from '@/hooks/useIsMobile';
import M_SelectPicker from '@/components/M_Molecules/M_SelectPicker/M_SelectPicker';

interface S_ActivationCapsulePopupProps {
  setIsActivationCapsulePopupOpen: (isOpen: boolean) => void;
  onSelectDate?: (date: Date) => void;
  initialDate?: Date;
}

export default function S_ActivationCapsulePopup({
  setIsActivationCapsulePopupOpen,
  onSelectDate,
  initialDate,
}: S_ActivationCapsulePopupProps) {
  const isMobile = useIsMobile(1080);
  const { language } = useLanguage();
  const t = useCallback(
    (key: string) => getTranslation(language, key),
    [language]
  );
  const initialBase = useMemo(() => initialDate ?? new Date(), [initialDate]);
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const base = new Date(initialBase);
    base.setSeconds(0, 0);
    return base;
  });
  const [time, setTime] = useState(() => ({
    hours: initialBase.getHours(),
    minutes: initialBase.getMinutes(),
  }));
  const [timeCounterSeed, setTimeCounterSeed] = useState(() =>
    initialBase.getTime()
  );
  const [activeTab, setActiveTab] = useState<'date' | 'time'>('date');

  const tabOptions = useMemo(
    () => [
      t('activationPopup.dateTab') || 'Дата',
      t('activationPopup.timeTab') || 'Время',
    ],
    [t]
  );

  const selectedTabLabel = useMemo(
    () => (activeTab === 'date' ? tabOptions[0] : tabOptions[1]),
    [activeTab, tabOptions]
  );

  const handleTabSelect = useCallback(
    (option: string) => {
      setActiveTab(option === tabOptions[0] ? 'date' : 'time');
    },
    [tabOptions]
  );

  useEffect(() => {
    const base = new Date(initialBase);
    base.setSeconds(0, 0);
    setSelectedDate(base);
    setTime({ hours: base.getHours(), minutes: base.getMinutes() });
    setTimeCounterSeed(base.getTime());
  }, [initialBase]);

  const handleClosePopup = () => {
    setIsActivationCapsulePopupOpen(false);
  };

  const handleCalendarChange = (date: Date) => {
    const next = new Date(date);
    next.setHours(time.hours, time.minutes, 0, 0);
    setSelectedDate(next);
  };

  const handleTimeChange = ({ hours, minutes }: TimeCounterValue) => {
    setTime({ hours, minutes });
    setSelectedDate(prev => {
      const next = new Date(prev);
      next.setHours(hours, minutes, 0, 0);
      return next;
    });
  };

  const handleConfirm = () => {
    onSelectDate?.(selectedDate);
    handleClosePopup();
  };

  if (isMobile) {
    return (
      <div className={styles.mobile_overlay}>
        <div className={styles.mobile_bottom_sheet}>
          <div className={styles.mobile_handle}></div>
          <div className={styles.mobile_content}>
            <div className={styles.mobile_section_wrapper}>
              <div className={styles.mobile_header}>
                <h4 className={styles.mobile_title}>
                  {t('activationPopup.title')}
                </h4>
                <M_SelectPicker
                  options={tabOptions}
                  selectedOption={selectedTabLabel}
                  onSelect={handleTabSelect}
                  background='white'
                />
              </div>
              <div className={styles.mobile_content_area}>
                {activeTab === 'date' ? (
                  <O_Calendar
                    value={selectedDate}
                    onChange={handleCalendarChange}
                  />
                ) : (
                  <O_TimeCounter
                    key={timeCounterSeed}
                    initial={{ hours: time.hours, minutes: time.minutes }}
                    units={['hours', 'minutes']}
                    onChange={handleTimeChange}
                    maxWidth='100%'
                  />
                )}
              </div>
            </div>
            <div className={styles.mobile_actions_row}>
              <A_Button
                variant='tertiary'
                size='small'
                fontSize='text-p-4'
                onClick={handleClosePopup}
              >
                {t('common.close')}
              </A_Button>
              <A_Button
                variant='primary'
                size='small'
                fontSize='text-p-4'
                onClick={handleConfirm}
              >
                {t('activationPopup.apply')}
              </A_Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.content_wrapper}>
        <div className={styles.wrapper}>
          <h4 className={styles.title}>{t('activationPopup.dateTitle')}</h4>
          <O_Calendar value={selectedDate} onChange={handleCalendarChange} />
        </div>
        <div className={styles.wrapper}>
          <h4 className={styles.title}>{t('activationPopup.timeTitle')}</h4>
          <O_TimeCounter
            key={timeCounterSeed}
            initial={{ hours: time.hours, minutes: time.minutes }}
            units={['hours', 'minutes']}
            onChange={handleTimeChange}
            visibleSlots={3}
          />
        </div>
      </div>
      <div className={styles.buttons_wrapper}>
        <A_Button
          variant='tertiary'
          size='small'
          fontSize='text-p-4'
          onClick={handleClosePopup}
        >
          {t('common.close')}
        </A_Button>
        <A_Button
          variant='primary'
          size='small'
          fontSize='text-p-4'
          onClick={handleConfirm}
        >
          {t('activationPopup.apply')}
        </A_Button>
      </div>
    </div>
  );
}
